"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import { workScopeStatusOptions } from "../../constants/options"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import FormYesNoInput from "../../../../shared/components/form-inputs/form-yes-no-input"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
const BASE_FIELD_NAME = "home.roughCarpentryInfo"
 

function HomeRoughCarpentaryForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillRoughCarpentryOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillRoughCarpentryOn` })
    
    // Watch status field and claimed by field
    const roughCarpentry = useWatch({ control, name: `${BASE_FIELD_NAME}.roughCarpentry` })
    const claimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.claimedBy` })
    const goodwillRoughCarpentry = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillRoughCarpentry` })
    const goodwillRoughCarpentryClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillRoughCarpentryClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: roughCarpentry,
        claimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.claimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillRoughCarpentry,
        claimedBy: goodwillRoughCarpentryClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillRoughCarpentryClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillRoughCarpentryOn,
    })

    return (
        <HomeDetailsWrapperAccordion heading="Rough Carpentry" wrapperClassName="xl:grid-cols-4">
            {/* Row 1: Rough Carpentry + Claimed By */}
            <FormSelectInput
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Rough Carpentry" toggleName={`${BASE_FIELD_NAME}.goodwillRoughCarpentryOn` as any} control={control} />,
                    wrapperClassName: "xl:col-span-2",
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.roughCarpentry`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Claimed By",
                    wrapperClassName: "xl:col-span-2",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.claimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillRoughCarpentryOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Rough Carpentry Goodwill", labelStyle: goodwillLabelStyle, wrapperClassName: "xl:col-span-2" }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillRoughCarpentry`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Rough Carpentry Goodwill Claimed By", wrapperClassName: "xl:col-span-2" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillRoughCarpentryClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            {/* Row 2: All yes/no questions + How many windows */}
            <FormYesNoInput
                inputGroupProps={{
                    label: "Does the house need walls repaired/rebuilt?",
                }}
                name={`${BASE_FIELD_NAME}.needsWallsRepaired`}
                control={control}
            />
            <FormYesNoInput
                inputGroupProps={{
                    label: "Does the house need subfloor repair?",
                }}
                name={`${BASE_FIELD_NAME}.needsSubfloorRepair`}
                control={control}
            />
            <FormYesNoInput
                inputGroupProps={{
                    label: "Does the house need windows?",
                }}
                name={`${BASE_FIELD_NAME}.needsWindows`}
                control={control}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "How many windows?",
                }}
                type="number"
                placeholder="How many windows?"
                name={`${BASE_FIELD_NAME}.numberOfWindows`}
                control={control}
            />
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{
                    label: "Give a brief description of the rough carpentry work needed including approximate materials",
                    wrapperClassName: "xl:col-span-4",
                }}
                rows={4}
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeRoughCarpentaryForm
